import React, { useState, useEffect } from 'react';
import { fluxAPI } from './api';
import './ImageGallery.css';

const ImageGallery = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrls, setImageUrls] = useState({});

  // 이미지 URL을 Blob으로 변환하여 로드
  useEffect(() => {
    const loadImages = async () => {
      const newUrls = {};

      for (const image of images) {
        // ✨ 이미 blobUrl이 있으면 사용 (즉시 표시)
        if (image.blobUrl) {
          newUrls[image.filename] = image.blobUrl;
        }
        // blobUrl이 없고 아직 로드하지 않은 경우에만 다운로드
        else if (!imageUrls[image.filename]) {
          try {
            const blobUrl = await fluxAPI.downloadImageAsBlob(image.filename);
            newUrls[image.filename] = blobUrl;
          } catch (error) {
            console.error(`이미지 로드 실패: ${image.filename}`, error);
          }
        }
      }

      if (Object.keys(newUrls).length > 0) {
        setImageUrls(prev => ({ ...prev, ...newUrls }));
      }
    };

    loadImages();

    // cleanup: blob URL 해제
    return () => {
      Object.values(imageUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [images]);

  const handleDownload = async (filename) => {
    try {
      await fluxAPI.downloadImage(filename);
    } catch (error) {
      console.error('다운로드 실패:', error);
      alert('이미지 다운로드에 실패했습니다.');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="image-gallery">
      <h2>생성된 이미지 ({images.length})</h2>

      {images.length === 0 ? (
        <div className="empty-gallery">
          <p>아직 생성된 이미지가 없습니다.</p>
          <p>위에서 프롬프트를 입력하고 이미지를 생성해보세요!</p>
        </div>
      ) : (
        <div className="gallery-grid">
          {images.map((image, index) => (
            <div key={index} className="gallery-item">
              <div
                className="image-container"
                onClick={() => setSelectedImage(image)}
              >
                {imageUrls[image.filename] ? (
                  <img
                    src={imageUrls[image.filename]}
                    alt={image.prompt}
                    loading="lazy"
                  />
                ) : (
                  <div className="image-loading">
                    <div className="spinner"></div>
                    <p>로딩 중...</p>
                  </div>
                )}
                <div className="image-overlay">
                  <span className="view-icon">🔍</span>
                </div>
              </div>

              <div className="image-info">
                <p className="image-prompt" title={image.prompt}>
                  {image.prompt.length > 100
                    ? `${image.prompt.substring(0, 100)}...`
                    : image.prompt
                  }
                </p>
                <div className="image-meta">
                  <span className="image-size">{image.width}×{image.height}</span>
                  <span className="image-time">{formatTimestamp(image.timestamp)}</span>
                </div>
                <button
                  className="download-btn"
                  onClick={() => handleDownload(image.filename)}
                >
                  ⬇️ 다운로드
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 이미지 모달 */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </button>

            {imageUrls[selectedImage.filename] ? (
              <img
                src={imageUrls[selectedImage.filename]}
                alt={selectedImage.prompt}
              />
            ) : (
              <div className="image-loading">
                <div className="spinner"></div>
                <p>로딩 중...</p>
              </div>
            )}

            <div className="modal-info">
              <h3>상세 정보</h3>
              <div className="modal-details">
                <p><strong>프롬프트:</strong> {selectedImage.prompt}</p>
                <p><strong>해상도:</strong> {selectedImage.width}×{selectedImage.height}</p>
                <p><strong>생성 시간:</strong> {formatTimestamp(selectedImage.timestamp)}</p>
                <p><strong>파일명:</strong> {selectedImage.filename}</p>
              </div>

              <button
                className="modal-download-btn"
                onClick={() => handleDownload(selectedImage.filename)}
              >
                ⬇️ 다운로드
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;